import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

// Interfaces
import { IChargebackAccountReference, IChargebackReferenceType } from "./interfaces";

// API
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// MUI
import { Box, Stack } from "@mui/material";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// Common Components
import Title from "../../../common/Title";

// Components
import ChargebackReference from "./ChargebackReference";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import clsx from "clsx";

const isReferenceInError = (references: IChargebackReferenceType[]) => {
	return references.some((reference) => {
		return !reference.label || !reference?.accountNumbers?.length;
	});
};

const getInitialReferences = (references: IChargebackReferenceType[], initialEmpty = false) => {
	if (initialEmpty && !references?.length) {
		return [
			{
				label: "",
				description: "",
				repairOrderRoute: "",
				accountNumbers: [],
			},
		];
	}
	return references;
};

const updateChargebackAccounts = (
	chargebackAccounts: IChargebackAccountReference[],
	references: IChargebackReferenceType[],
	position: string
) => {
	const previousIndex = chargebackAccounts.findIndex((account) => account.position === position);
	if (previousIndex === -1) {
		return [...chargebackAccounts, { position, chargebackTypes: references }];
	}
	const updated = [...chargebackAccounts];
	updated[previousIndex]["chargebackTypes"] = [...references];
	return updated;
};

const ChargebacksAccountReferences = (props: {
	locationId: string;
	position: string;
	chargebacksReferences: IChargebackReferenceType[];
	chargebackAccountReferences: IChargebackAccountReference[];
}) => {
	const { locationId, position, chargebackAccountReferences, chargebacksReferences = [] } = props;
	const [innerReferences, setInnerReferences] = useState<IChargebackReferenceType[]>([]);

	const { mutate, isPending } = useMutation({
		mutationFn: (input: { chargebackAccountReferences: IChargebackAccountReference[] }) =>
			updateSystemSettings(locationId, input, "CHARGEBACK_ACCOUNT_REFERENCES"),
		onError: (error) => errorLogger(error),
	});

	const update = (references: IChargebackReferenceType[]) => {
		setInnerReferences(references);
		const isErrorDetected = isReferenceInError(references);
		if (!isErrorDetected) {
			const updated = updateChargebackAccounts(chargebackAccountReferences, references, position);
			mutate({ chargebackAccountReferences: updated });
		}
	};

	const handleUpdateReferences = (reference: IChargebackReferenceType, index = 0) => {
		const updated = [...innerReferences];
		updated[index] = reference;
		update(updated);
	};

	const handleAddReference = () => {
		setInnerReferences([
			...innerReferences,
			{
				accountNumbers: [],
				label: "",
				description: "",
				repairOrderRoute: "",
			},
		]);
	};

	const handleDeleteReference = (index: number) => {
		const updated = [...innerReferences];
		if (index !== -1) {
			updated.splice(index, 1);
			update(updated);
		}
	};

	useEffect(() => {
		setInnerReferences(getInitialReferences(chargebacksReferences));
		return () => {
			setInnerReferences([]);
		};
	}, [locationId, chargebacksReferences]);

	const isError = isReferenceInError(innerReferences);
	const isDisabledAdd = isError || isPending;

	return (
		<Box>
			<Title title="Chargeback Types" className="!mb-[24px]" />
			<Stack mt="15px">
				<Stack spacing={2}>
					{innerReferences.map((chargebackReference, index) => (
						<ChargebackReference
							key={`${position}_${index}`}
							index={index}
							accountNumbers={chargebackReference?.accountNumbers ?? []}
							label={chargebackReference?.label ?? ""}
							readOnly={false}
							saving={isPending}
							onActionUpdate={(reference) => handleUpdateReferences(reference as IChargebackReferenceType, index)}
							onActionDelete={() => handleDeleteReference(index)}
						/>
					))}
				</Stack>
			</Stack>
			<Stack mt={1.5}>
				<Button
					variant="contained"
					text="Add CB Type"
					classes={clsx({
						"hover:!shadow-primary-shadow-50 h-[30px] !rounded-[6px] !text-white btn-regular-text": true,
						"!bg-primary": !isDisabledAdd,
						"!bg-disabled": isDisabledAdd,
					})}
					endIcon={<AddCircleRounded />}
					sx={{ height: "30px", width: "125px" }}
					disabled={isDisabledAdd}
					loading={isPending}
					onClick={handleAddReference}
				/>
			</Stack>
		</Box>
	);
};

export default ChargebacksAccountReferences;
